import moment from 'moment'

export default class RuleBuilderEvaluator {

    /**
     * Negierte Operatoren haben verfolgen Teils eine andere Logik bei der Auswertung.
     * (z.b. bei nicht vorhandener Antwort auf eine Frage sind diese true,
     * oder bei mehreren Antworten auf eine Frage muss nur eine der Antworten false sein damit die Regel als false angesehen wird.)
     * @type {[string]}
     */
    static negatedOperators = ['!=', '!_%', '!%_%', '!%_', 'notIn', 'isNotBetween']

    /**
     * Numerische Operatoren lösen eine Typumwandlung von Vergleichswerten aus. Diese sollten bei String Operatoren nicht
     * auftreten, da es sonst zu ungewollten Nebeneffekten kommt.
     * (z.b. werden Vergleiche mit Strings der Form '08' zu einer Zahl 8 umgewandelt, was der Abfrage entgegenwirkt.)
     * @type {[string]}
     */
    static numericOperators = ['>', '>=', '<=', '<', '[,]']

    /**
     * @param {Object} comparisonElementValues - Object of Key Value Pairs with: Name, city, zip, answers
     *                 or whatever the future brings us
     * @param {?boolean} invalidComparisonEvaluatesTo - If null, an Exception will be thrown if we have
     *                   missing comparisonElementValues, operatorValue or answerValue
     *                   Otherwise the invalid comparison evaluates to this value
     */
    constructor(comparisonElementValues, invalidComparisonEvaluatesTo = null) {
        this.comparisonElementValues = comparisonElementValues
        this.invalidComparisonEvaluatesTo = invalidComparisonEvaluatesTo
    }

    /**
     * @param {Object} rule - The rule, we all know and love
     */
    evaluateRule(rule) {
        return this._evaluateCondition(rule)
    }

    _evaluateCondition(condition) {
        if (condition.type === "orCondition")
            return this._evaluateOrCondition(condition)
        else if (condition.type === "andCondition")
            return this._evaluateAndCondition(condition)
        else if (condition.type === "ruleCondition")
            return this._evaluateRuleCondition(condition)
    }

    _evaluateOrCondition(condition) {
        for (let childCondition of condition.conditions) {
            //Return immediately if one rule is valid
            if (this._evaluateCondition(childCondition)) {
                return true;
            }
        }
        //If no Rule was true, return false
        return false;
    }

    _evaluateAndCondition(condition) {
        for (let childCondition of condition.conditions) {
            //Return immediately if one rule is not valid
            if (!this._evaluateCondition(childCondition)) {
                return false;
            }
        }
        //If all rules were true, return true
        return true;
    }

    _evaluateRuleCondition(condition) {
        try {
            let comparisonElementFunctionString =
                '_evaluate' +
                condition.comparisonElement.charAt(0).toUpperCase() + condition.comparisonElement.slice(1).toLowerCase()
                + 'Condition'

            let functionToCall = this[comparisonElementFunctionString]

            if (typeof functionToCall === "function") {
                return functionToCall(condition)
            } else {
                return this._evaluateDefaultCondition(condition)
            }
        } catch (e) {
            if (this.invalidComparisonEvaluatesTo !== null) {
                return this.invalidComparisonEvaluatesTo
            } else {
                throw e;
            }
        }

    }

    _evaluateTrueCondition() {
        return true
    }

    _evaluateFalseCondition() {
        return false
    }

    _evaluateDefaultCondition(condition) {
        let comparisonElementValues = this.comparisonElementValues[condition.comparisonElement]
        let operatorValue = condition.operator.value
        let answerValue = condition.answerValue

        if (operatorValue == null) {
            throw "Missing operatorValue"
        }

        //answerValue is the value of rule builder answer
        if (answerValue == null) {
            throw "Missing answerValue"
        }

        //comparisonElementValue the is value of user input
        if (comparisonElementValues == null) {
            if (this.invalidComparisonEvaluatesTo === true) {
                return true
            } else if (this.invalidComparisonEvaluatesTo === false) {
                //Achtung: Wenn eine Antwort nicht vorhanden ist, müssen wir überprüfen,
                //ob die hinterlegte Regel eine negierte Regel ist, da in diesem fall wir die Regel
                //als wahr ansehen, wenn keine Antwort gegeben worden ist.
                return RuleBuilderEvaluator.negatedOperators.includes(operatorValue)
            } else {
                throw "Missing comparisonElementValues"
            }
        }

        if (Array.isArray(comparisonElementValues)) {
            //Bei mehreren Antworten auf eine Frage muss nur eine der Antworten false sein damit die Regel als false angesehen wird.
            if (RuleBuilderEvaluator.negatedOperators.includes(operatorValue)) {
                for (let oneComparisonElementValue of comparisonElementValues) {
                    if (!this._compareConditionValues(oneComparisonElementValue, operatorValue, answerValue))
                        return false
                }
                return true
            }

            //Bei mehreren Antworten auf eine Frage muss nur eine der Antworten true sein damit die Regel als true angesehen wird.
            else {
                for (let oneComparisonElementValue of comparisonElementValues) {
                    if (this._compareConditionValues(oneComparisonElementValue, operatorValue, answerValue))
                        return true
                }
                return false
            }
        } else {
            return this._compareConditionValues(comparisonElementValues, operatorValue, answerValue)
        }

    }

    //comparisonElementValue is the value of user input
    //answerValue is the value of rule builder answer
    _compareConditionValues(comparisonElementValue, operatorValue, answerValue) {
        //Convert Numbers to real numbers before comparison
        if (RuleBuilderEvaluator.numericOperators.includes(operatorValue) && !isNaN(comparisonElementValue)) {
            comparisonElementValue = Number(comparisonElementValue)
        }
        if (RuleBuilderEvaluator.numericOperators.includes(operatorValue)) {
            if(operatorValue === "[,]" && Array.isArray(answerValue)){
                answerValue[0] = Number(answerValue[0])
                answerValue[1] = Number(answerValue[1])
            } else if(!isNaN(answerValue) && !Array.isArray(answerValue)) { //isNaN([]) === false
                answerValue = Number(answerValue)
            }
        }

        switch (operatorValue) {
            case "=":
                return comparisonElementValue == answerValue
            case "!=":
                return comparisonElementValue != answerValue
            case ">":
                return comparisonElementValue > answerValue
            case ">=":
                return comparisonElementValue >= answerValue
            case "<=":
                return comparisonElementValue <= answerValue
            case "<":
                return comparisonElementValue < answerValue
            case "[,]":
                return comparisonElementValue >= answerValue[0] && comparisonElementValue <= answerValue[1]
            case "_%":
                return Array.isArray(answerValue) ?
                    answerValue.some(value => comparisonElementValue.toString().startsWith(value)) :
                    comparisonElementValue.toString().startsWith(answerValue)
            case "%_%":
                return Array.isArray(answerValue) ?
                    answerValue.some(value => comparisonElementValue.toString().includes(value)) :
                    comparisonElementValue.toString().includes(answerValue)
            case "%_":
                return Array.isArray(answerValue) ?
                    answerValue.some(value => comparisonElementValue.toString().endsWith(value)) :
                    comparisonElementValue.toString().endsWith(answerValue)
            case "!_%":
                return Array.isArray(answerValue) ?
                    !answerValue.some(value => comparisonElementValue.toString().startsWith(value)) :
                    !comparisonElementValue.toString().startsWith(answerValue)
            case "!%_%":
                return Array.isArray(answerValue) ?
                    !answerValue.some(value => comparisonElementValue.toString().includes(value)) :
                    !comparisonElementValue.toString().includes(answerValue)
            case "!%_":
                return Array.isArray(answerValue) ?
                    !answerValue.some(value => comparisonElementValue.toString().endsWith(value)) :
                    !comparisonElementValue.toString().endsWith(answerValue)
            case "in":
                return answerValue.some(value => value == comparisonElementValue)
            case "notIn":
                return !answerValue.some(value => value == comparisonElementValue)
            case "isBetween":
                return this._compareIsBetweenValues(comparisonElementValue, answerValue)
            case "isNotBetween":
                return !this._compareIsBetweenValues(comparisonElementValue, answerValue)
            case "from":
                return moment(comparisonElementValue).isSameOrAfter(answerValue)
            case "till":
                return moment(comparisonElementValue).isSameOrBefore(answerValue)
            case "isMonth":
                // moment js uses 0 based index for months
                return moment(comparisonElementValue).month() === (answerValue - 1)
            default:
                throw "Invalid operator"
        }
    }

    _compareIsBetweenValues(comparisonElementValue, answerValue) {
        let date = moment(comparisonElementValue)
        let start = moment(answerValue[0])
        let end = moment(answerValue[1])
        if (date.isValid() && start.isValid() && end.isValid()) {
            return date.isBetween(start, end);
        } else {
            return comparisonElementValue >= answerValue[0] &&
                comparisonElementValue <= answerValue[1]
        }
    }

}
